import React from "react";

import { Alert } from "@chakra-ui/react";
import { useAuth, useCustomToast } from "@hooks";

import { Text } from "@chakra-ui/react";
import { DocumentData, WithFieldValue } from "firebase/firestore";
import { convertDateToTimestamp, convertTimestampToDate } from "@utils";

import { useStoreTags } from "@hooks";
import { FirebaseContext } from "@contexts";

import { StepDb } from "@constants";

import { query, collection, getDocs, orderBy, where } from "firebase/firestore";
import { DB_COLLECTION_NAMES } from "@constants";

import { makeDateControl, JsonForm, JsonFormControl } from "../JsonForm";
import { ButtonModal } from "../ModalWrappers";

function download(filename: string, text: string) {
  const element = document.createElement("a");
  element.setAttribute(
    "href",
    "data:text/plain;charset=utf-8," + encodeURIComponent(text),
  );
  element.setAttribute("download", filename);

  element.style.display = "none";
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

function makeAssemblyReportsControl() {
  return [
    makeDateControl("Minimum date (mm-dd-yyyy)", "minDate"),
    makeDateControl("Maximum date (mm-dd-yyyy)", "maxDate"),
  ] as JsonFormControl[];
}

type StepCount = {
  brown: number;
  fermilab: number;
  princeton: number;
  rutgers: number;
};

export function MakeAssemblyReportsModal() {
  const tags = useStoreTags();
  const tagFormControl = React.useMemo(
    () => makeAssemblyReportsControl(),
    [tags],
  );
  useAuth();

  const [error] = React.useState("");

  const { firestore } = React.useContext(FirebaseContext);
  const collectionName = DB_COLLECTION_NAMES.STEPS;

  const tagAddedToast = useCustomToast(() => (
    <Alert status="success">Data downloaded!</Alert>
  ));

  const collectionRef = collection(firestore, collectionName);

  const doSubmit = React.useCallback(async function <
    T extends WithFieldValue<DocumentData>,
  >(data: T) {
    // Find the number of steps from this module that contain the right text
    // that indicates that this has already been done.
    const minDateParsing = data.minDate.split("-");
    const maxDateParsing = data.maxDate.split("-");
    // It seems the dates count from 0, but obviously  we do not write them this way
    const tmpMinDate = new Date(
      Number(minDateParsing[2]),
      Number(minDateParsing[0]) - 1,
      Number(minDateParsing[1]),
      0,
      0,
      0,
    );
    // Adding 1 to the max date to enable timestamp comparison for things past midnight
    const tmpMaxDate = new Date(
      Number(maxDateParsing[2]),
      Number(maxDateParsing[0]) - 1,
      Number(maxDateParsing[1]) + 1,
      0,
      0,
      0,
    );
    const myMinDate = convertDateToTimestamp(tmpMinDate);
    const myMaxDate = convertDateToTimestamp(tmpMaxDate);

    // Map users to locations
    // TODO: currently, this doesn't work if two people have the same first and last name,
    // but given the number of people using this DB, it shouldn't be a problem (hopefully)
    const userMap = new Map<string, string>();

    const collectionNameUsers = DB_COLLECTION_NAMES.USERS;
    const collectionRefUsers = collection(firestore, collectionNameUsers);
    const userQueryResult = query(collectionRefUsers);

    Promise.all([getDocs(userQueryResult)]).then(([users]) => {
      users.forEach((userDoc) => {
        userMap.set(
          userDoc.data().firstName + " " + userDoc.data().lastName,
          userDoc.data().location,
        );
      });
    });

    const stepMap = new Map<string, StepCount>();
    const sensorMap = new Map<string, string>();
    const completeMap = new Map<string, typeof sensorMap>();
    const queryResult = query(
      collectionRef,
      // Implement time dependence
      orderBy("timestamp"),
      where("timestamp", ">=", myMinDate),
      where("timestamp", "<=", myMaxDate),
    );
    let dbString = "moduleId Brown Fermilab Princeton Rutgers\n";
    let dbStringComplete = "StepName Module Date\n";

    Promise.all([getDocs(queryResult)]).then(([allSteps]) => {
      allSteps.forEach((doc) => {
        const step = doc.data() as StepDb;
        let cStepName = step.stepName as string;
        if (
          !step.stepName.includes("AUX") &&
          !step.stepName.includes("IMPORT")
        ) {
          const location = userMap.get(step.userName)
            ? userMap.get(step.userName)
            : "";

          if (step.stepName.includes("2S_SENSOR_KAPTON_GLUING")) {
            if (step.details.stumps == 1) {
              cStepName = cStepName + "_5";
            } else {
              cStepName = cStepName + "_6";
            }
          }
          if (step.stepName.includes("2S_SENSOR_HV_TAIL_GLUING")) {
            if (step.details.orientation == "top") {
              cStepName = cStepName + "_top";
            } else {
              cStepName = cStepName + "_bottom";
            }
          }
          const isBrown = location == "Brown" ? 1 : 0;
          const isFermilab = location == "Fermilab" ? 1 : 0;
          const isPrinceton = location == "Princeton" ? 1 : 0;
          const isRutgers = location == "Rutgers" ? 1 : 0;
          const cStep = stepMap.get(cStepName);
          const stepCount: StepCount = {
            brown: isBrown + (cStep ? cStep.brown : 0),
            fermilab: isFermilab + (cStep ? cStep.fermilab : 0),
            princeton: isPrinceton + (cStep ? cStep.princeton : 0),
            rutgers: isRutgers + (cStep ? cStep.rutgers : 0),
          };
          stepMap.set(cStepName, stepCount);

          if (!cStep) {
            const moduleMap = new Map<string, string>();
            const dateVal = convertTimestampToDate(step.timestamp);
            const month = dateVal.getMonth() + 1;
            moduleMap.set(
              step.mainComponentSerial,
              month + "-" + dateVal.getDate() + "-" + dateVal.getFullYear(),
            );
            completeMap.set(cStepName, moduleMap);
          }
        }
      });

      stepMap.forEach((value: StepCount, step: string) => {
        dbString =
          dbString +
          step +
          " " +
          value.brown +
          " " +
          value.fermilab +
          " " +
          value.princeton +
          " " +
          value.rutgers +
          "\n";
      });

      completeMap.forEach((value: typeof sensorMap, step: string) => {
        value.forEach((date: string, sensor: string) => {
          dbStringComplete =
            dbStringComplete + step + " " + sensor + " " + date + "\n";
        });
      });

      tagAddedToast();
      download(
        "assemblyReport_from_" + data.minDate + "_to_" + data.maxDate + ".csv",
        dbString,
      );
      download(
        "assemblyReportFull_from_" +
          data.minDate +
          "_to_" +
          data.maxDate +
          ".csv",
        dbStringComplete,
      );
    });
  }, []);

  return (
    <ButtonModal
      label="Download Assembly Reports"
      title="Download Assembly Reports"
      size="sm"
    >
      <JsonForm fields={tagFormControl} onSubmit={doSubmit} clearAfterSubmit />
      {error ? <Text>{error}</Text> : undefined}
    </ButtonModal>
  );
}
